import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";

const AdminHomepage = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSection, setEditingSection] = useState(null);
  const [showModal, setShowModal] = useState(false);
  
  // For product selection
  const [allProducts, setAllProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProductIDs, setSelectedProductIDs] = useState([]);

  useEffect(() => {
    fetchSections();
    fetchProducts();
  }, []);

  const fetchSections = async () => {
    try {
      const res = await api.get("/home-sections");
      setSections(res.data);
      setLoading(false);
    } catch (error) {
      console.error("❌ Error fetching sections:", error);
      toast.error("Không thể tải danh sách các mục hiển thị");
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await api.get("/products?limit=9999");
      setAllProducts(res.data.products || []);
    } catch (error) {
      console.error("❌ Error fetching products:", error);
    }
  };

  const handleEdit = (section) => {
    setEditingSection({ ...section });
    setSelectedProductIDs(section.productIds || []);
    setShowModal(true);
  };

  const toggleProduct = (pid) => {
    if (selectedProductIDs.includes(pid)) {
      setSelectedProductIDs(selectedProductIDs.filter((id) => id !== pid));
    } else {
      setSelectedProductIDs([...selectedProductIDs, pid]);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      await api.put(`/admin/home-sections/${editingSection._id}`, {
        title: editingSection.title,
        productIds: selectedProductIDs,
        isActive: editingSection.isActive
      });
      toast.success("Cập nhật thành công!");
      setShowModal(false);
      fetchSections();
    } catch (error) {
      console.error("❌ Save error:", error);
      toast.error("Cập nhật thất bại");
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = allProducts.filter(p => 
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.brand?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading && sections.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-8 bg-gray-50 min-h-screen font-inter">
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-black text-slate-800 tracking-tight">Quản lý Trang chủ</h1>
        <p className="text-sm text-slate-400 font-bold uppercase tracking-widest mt-1">Tùy chỉnh tiêu đề và sản phẩm hiển thị</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {sections.map((section) => (
          <div key={section._id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-all">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-black text-slate-800">{section.title}</h3>
                  <p className="text-[10px] text-slate-400 font-bold uppercase tracking-tighter">Key: {section.key}</p>
                </div>
                <span className={`px-2 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider ${section.isActive ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                  {section.isActive ? 'Đang bật' : 'Đã tắt'}
                </span>
              </div>
              <p className="text-sm text-slate-500 mb-6">
                <span className="font-bold text-slate-800">{section.productIds?.length || 0}</span> sản phẩm được chọn
              </p>
              <button
                onClick={() => handleEdit(section)}
                className="w-full py-3 bg-slate-900 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:bg-black transition-all"
              >
                Chỉnh sửa
              </button>
            </div>
          </div>
        ))}
      </div>

      {showModal && editingSection && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="bg-white w-full max-w-4xl h-full sm:h-[90vh] rounded-[32px] overflow-hidden shadow-2xl flex flex-col animate-modalUp">
            <div className="p-6 bg-slate-900 text-white flex justify-between items-center">
              <h3 className="font-black uppercase tracking-widest">Chỉnh sửa mục: {editingSection.key}</h3>
              <button onClick={() => setShowModal(false)} className="w-10 h-10 rounded-full hover:bg-slate-800 flex items-center justify-center bg-slate-800">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6 no-scrollbar">
              <div>
                <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Tiêu đề hiển thị</label>
                <input
                  type="text"
                  value={editingSection.title}
                  onChange={(e) => setEditingSection({ ...editingSection, title: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-bold"
                />
              </div>

              <div className="flex items-center gap-3">
                 <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={editingSection.isActive}
                      onChange={(e) => setEditingSection({...editingSection, isActive: e.target.checked})}
                      className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-black text-slate-700 uppercase tracking-widest">Kích hoạt mục này</span>
                 </label>
              </div>

              <div>
                <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Chọn sản phẩm ({selectedProductIDs.length})</label>
                <div className="relative mb-4">
                   <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-300">search</span>
                   <input
                     type="text"
                     placeholder="Tìm tên giày..."
                     className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-100 rounded-xl outline-none text-sm transition-all focus:ring-2 focus:ring-blue-500"
                     value={searchTerm}
                     onChange={(e) => setSearchTerm(e.target.value)}
                   />
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-[400px] overflow-y-auto p-1 no-scrollbar">
                  {filteredProducts.map((p) => {
                    const isSelected = selectedProductIDs.includes(p._id);
                    return (
                      <div 
                        key={p._id} 
                        onClick={() => toggleProduct(p._id)}
                        className={`cursor-pointer group relative p-2 rounded-2xl border-2 transition-all ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-slate-50 hover:border-slate-200'}`}
                      >
                        <img src={p.image} alt={p.name} className="w-full aspect-square object-cover rounded-xl mb-2" />
                        <p className="text-[10px] font-black text-slate-800 line-clamp-2 leading-tight">{p.name}</p>
                        {isSelected && (
                          <div className="absolute top-2 right-2 bg-blue-600 text-white rounded-full p-0.5 shadow-lg">
                            <span className="material-symbols-outlined text-[14px]">check</span>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>

            <div className="p-6 bg-gray-50 border-t flex gap-3">
               <button onClick={() => setShowModal(false)} className="flex-1 py-4 font-black text-xs text-slate-400 uppercase tracking-widest">Hủy</button>
               <button 
                 onClick={handleSave} 
                 disabled={loading}
                 className="flex-[2] py-4 bg-slate-900 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-black transition-all shadow-xl shadow-slate-200"
               >
                 {loading ? 'Đang lưu...' : 'Lưu cài đặt'}
               </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminHomepage;
